import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  FaMapMarkerAlt, FaClock, FaPhone, FaEnvelope, FaLeaf,
  FaStar, FaHeart, FaPray, FaCheckCircle, FaTimes, FaSearch,
  FaOm, FaFire
} from "react-icons/fa";
import Navbar from "./Navbar";
import Footer from "./Footer";

/* ─────────────────────────────────────────────────────
   TOAST
───────────────────────────────────────────────────── */
function Toast({ message, type, onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 5000);
    return () => clearTimeout(t);
  }, [onClose]);
  

  const cfg = {
    success: { icon: <FaCheckCircle />, label: "Success", accent: "#2a6b2a", iconBg: "rgba(42,107,42,0.12)", border: "rgba(42,107,42,0.3)", symbol: "✦" },
    error:   { icon: <FaTimes />,       label: "Error",   accent: "#8b1a1a", iconBg: "rgba(139,26,26,0.10)", border: "rgba(139,26,26,0.28)", symbol: "✗" },
    info:    { icon: <FaSearch />,      label: "Notice",  accent: "#8a6020", iconBg: "rgba(201,151,58,0.10)", border: "rgba(201,151,58,0.30)", symbol: "ॐ" },
  };
  const c = cfg[type] || cfg.info;

  return (
    <div className="ab-toast-wrap">
      <div className="ab-toast" style={{ "--accent": c.accent, "--border": c.border }}>
        <div className="ab-toast-bar" />
        <span className="ab-tc tl" /><span className="ab-tc tr" />
        <span className="ab-tc bl" /><span className="ab-tc br" />
        <div className="ab-toast-medal" style={{ background: c.iconBg, borderColor: c.border }}>
          <div className="ab-toast-spin" />
          <span style={{ color: c.accent, fontSize: 14, position: "relative", zIndex: 1 }}>{c.icon}</span>
        </div>
        <div className="ab-toast-body">
          <div className="ab-toast-row">
            <span style={{ color: c.accent, fontSize: 9 }}>{c.symbol}</span>
            <span className="ab-toast-title" style={{ color: c.accent }}>{c.label}</span>
            <span style={{ color: c.accent, fontSize: 9 }}>{c.symbol}</span>
          </div>
          <div className="ab-toast-rule" style={{ background: `linear-gradient(90deg,transparent,${c.accent}55,transparent)` }} />
          <p className="ab-toast-msg">{message}</p>
        </div>
        <button className="ab-toast-x" onClick={onClose} style={{ color: c.accent }}>✕</button>
        <div className="ab-toast-foot" />
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────
   STYLES
───────────────────────────────────────────────────── */
function AbStyles() {
  return (
    <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Cinzel+Decorative:wght@400;700;900&family=Cinzel:wght@400;500;600;700&family=EB+Garamond:ital,wght@0,400;0,500;0,600;1,400;1,500&display=swap');

      :root {
        --gold-bright: #e8c46a; --gold-mid: #c9973a; --gold-dim: #8a6020;
        --crimson-deep: #1a0404; --crimson-mid: #2c0808; --crimson-rich: #3d0c0c;
        --hero-1: #2e0c0c; --hero-2: #1c0505; --hero-3: #100202;
        --parchment: #FDF3E3; --parchment-dark: #f5e8cc;
        --text-dark: #1a0404; --text-mid: #4a1a08;
      }
      *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }

      /* ══ HERO ══ */
      .ab-hero { display: grid; grid-template-columns: 72px 1fr 72px; align-items: stretch; position: relative; overflow: hidden; background: linear-gradient(160deg, var(--hero-1) 0%, var(--hero-2) 50%, var(--hero-3) 100%); border-bottom: 1px solid rgba(201,151,58,0.18); }
      .ab-hero-bg { position: absolute; inset: 0; pointer-events: none; background: radial-gradient(ellipse at 15% 30%, rgba(160,35,35,0.35) 0%, transparent 50%), radial-gradient(ellipse at 85% 70%, rgba(80,15,15,0.3) 0%, transparent 45%); }
      .ab-hero-rings { position: absolute; top: 30px; right: 60px; width: 230px; height: 230px; pointer-events: none; }
      .ab-hero-ring { position: absolute; top: 50%; left: 50%; transform: translate(-50%,-50%); border-radius: 50%; border: 1px solid rgba(232,196,106,0.07); animation: abRingPulse 4s ease-in-out infinite alternate; }
      .ab-hero-ring:nth-child(odd) { border-style: dashed; }
      @keyframes abRingPulse { from { opacity: 0.3; } to { opacity: 0.8; } }

      /* Medallion */
      .ab-hero-medallion { width: 90px; height: 90px; border-radius: 50%; background: radial-gradient(circle at 40% 35%, #5a1515 0%, #2a0707 55%, #0e0101 100%); border: 1px solid rgba(232,196,106,0.3); display: flex; align-items: center; justify-content: center; position: relative; margin-bottom: 20px; box-shadow: 0 0 30px rgba(232,196,106,0.15), inset 0 1px 0 rgba(255,255,255,0.06); }
      .ab-hero-med-ring { position: absolute; inset: -5px; border-radius: 50%; border: 1px dashed rgba(232,196,106,0.25); animation: abSlowSpin 10s linear infinite; }
      .ab-hero-med-ring-2 { inset: -10px; border-style: dotted; border-color: rgba(232,196,106,0.12); animation-duration: 18s; animation-direction: reverse; }
      @keyframes abSlowSpin { to { transform: rotate(360deg); } }
      .ab-hero-face { position: absolute; inset: 0; border-radius: 50%; display: flex; align-items: center; justify-content: center; opacity: 0; transform: scale(0.7) rotateY(90deg); transition: opacity 0.55s ease, transform 0.55s ease; }
      .ab-hero-face.visible { opacity: 1; transform: scale(1) rotateY(0deg); }
      .ab-hero-om { font-family: "Cinzel Decorative", cursive; font-size: 34px; background: linear-gradient(180deg, #fff8e7, var(--gold-bright), var(--gold-mid)); -webkit-background-clip: text; -webkit-text-fill-color: transparent; filter: drop-shadow(0 0 8px rgba(232,196,106,0.5)); }
      .ab-hero-logo-img { width: 54px; height: 54px; object-fit: contain; filter: drop-shadow(0 0 8px rgba(232,196,106,0.4)); }
      .ab-hero-med-halo { position: absolute; inset: -16px; border-radius: 50%; background: radial-gradient(circle, rgba(232,196,106,0.08) 0%, transparent 65%); pointer-events: none; }

      /* Center */
      .ab-hero-center { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 40px 32px 32px; position: relative; z-index: 2; text-align: center; }
      .ab-hero-eyebrow { font-family: "Cinzel", serif; font-size: 8.5px; letter-spacing: 2px; color: rgba(232,196,106,0.35); text-transform: uppercase; margin-bottom: 10px; }
      .ab-hero-title { font-family: "Cinzel Decorative", cursive; font-size: 28px; font-weight: 900; background: linear-gradient(135deg, #fffaf0, var(--gold-bright), var(--gold-mid)); -webkit-background-clip: text; -webkit-text-fill-color: transparent; line-height: 1.2; margin-bottom: 8px; }
      .ab-hero-sub { font-family: "EB Garamond", serif; font-style: italic; font-size: 15px; color: rgba(245,228,176,0.4); margin-bottom: 28px; letter-spacing: 0.3px; }
      .ab-hero-rule { width: 60%; max-width: 400px; height: 1px; margin: 0 auto; background: linear-gradient(90deg, transparent, rgba(201,151,58,0.4), transparent); }

      /* Side columns */
      .ab-hero-side-col { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 8px; padding: 28px 0; position: relative; z-index: 2; }
      .ab-side-left { border-right: 1px solid rgba(201,151,58,0.12); }
      .ab-side-right { border-left: 1px solid rgba(201,151,58,0.12); }
      .ab-hlc-om { font-family: "Cinzel Decorative", cursive; font-size: 20px; background: linear-gradient(180deg, #fff8e7, var(--gold-bright), var(--gold-mid)); -webkit-background-clip: text; -webkit-text-fill-color: transparent; filter: drop-shadow(0 0 8px rgba(232,196,106,0.5)); line-height: 1; }
      .ab-hlc-line { width: 1px; flex: 1; min-height: 16px; background: linear-gradient(180deg, transparent, rgba(201,151,58,0.3), transparent); }
      .ab-hlc-symbol { font-size: 16px; color: rgba(232,196,106,0.5); line-height: 1; }
      .ab-hlc-tick { width: 2px; border-radius: 1px; background: linear-gradient(180deg, var(--gold-bright), var(--gold-mid)); }
      .ab-hlc-top-ornament, .ab-hlc-bot-ornament { display: flex; flex-direction: column; align-items: center; gap: 3px; }

      /* ══ CONTENT ══ */
      .ab-body { background: var(--parchment); padding: 60px 28px 80px; }
      .ab-inner { max-width: 1000px; margin: 0 auto; }

      /* Section header */
      .ab-sec-head { text-align: center; margin-bottom: 48px; }
      .ab-sec-eyebrow { font-family: "Cinzel", serif; font-size: 8.5px; letter-spacing: 3px; text-transform: uppercase; color: var(--gold-dim); margin-bottom: 10px; }
      .ab-sec-title { font-family: "Cinzel Decorative", cursive; font-size: 22px; font-weight: 700; color: var(--crimson-deep); margin-bottom: 12px; }
      .ab-sec-rule { width: 80px; height: 2px; margin: 0 auto; background: linear-gradient(90deg, transparent, var(--gold-mid), transparent); }

      /* History paragraph */
      .ab-history { font-family: "EB Garamond", serif; font-size: 17px; color: var(--text-mid); line-height: 1.9; text-align: center; max-width: 780px; margin: 0 auto 60px; }
      .ab-history p + p { margin-top: 18px; }

      /* Feature cards */
      .ab-features { display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px; margin-bottom: 60px; }
      .ab-feature-card { position: relative; padding: 32px 24px; background: #0e0101; border: 1px solid rgba(232,196,106,0.12); clip-path: polygon(0 0, calc(100% - 18px) 0, 100% 18px, 100% 100%, 18px 100%, 0 calc(100% - 18px)); overflow: hidden; }
      .ab-feature-card::before { content: ''; position: absolute; inset: 0; background: radial-gradient(ellipse at 30% 20%, rgba(160,35,35,0.18) 0%, transparent 65%); pointer-events: none; }
      .ab-feature-icon { width: 48px; height: 48px; border-radius: 50%; background: radial-gradient(circle at 40% 35%, #5a1515 0%, #2a0707 100%); border: 1px solid rgba(232,196,106,0.25); display: flex; align-items: center; justify-content: center; font-size: 18px; color: var(--gold-bright); margin-bottom: 16px; position: relative; z-index: 1; box-shadow: 0 0 16px rgba(232,196,106,0.1); }
      .ab-feature-title { font-family: "Cinzel", serif; font-size: 12px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; color: var(--gold-bright); margin-bottom: 10px; position: relative; z-index: 1; }
      .ab-feature-text { font-family: "EB Garamond", serif; font-size: 15px; color: rgba(232,196,106,0.5); line-height: 1.7; position: relative; z-index: 1; }

      /* Info strip */
      .ab-info-strip { display: grid; grid-template-columns: repeat(2, 1fr); gap: 24px; margin-bottom: 60px; }
      .ab-info-card { background: #fff8f0; border: 1px solid rgba(201,151,58,0.2); padding: 28px 28px; clip-path: polygon(0 0, calc(100% - 14px) 0, 100% 14px, 100% 100%, 14px 100%, 0 calc(100% - 14px)); }
      .ab-info-card-title { font-family: "Cinzel", serif; font-size: 10px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; color: var(--gold-dim); margin-bottom: 18px; display: flex; align-items: center; gap: 8px; }
      .ab-info-card-title svg { color: var(--gold-mid); }
      .ab-info-row { display: flex; align-items: flex-start; gap: 10px; margin-bottom: 12px; font-family: "EB Garamond", serif; font-size: 15.5px; color: var(--text-mid); }
      .ab-info-row svg { color: var(--gold-mid); font-size: 13px; flex-shrink: 0; margin-top: 3px; }
      .ab-info-row:last-child { margin-bottom: 0; }

      /* Deities section */
      .ab-deities { display: grid; grid-template-columns: repeat(5, 1fr); gap: 18px; margin-bottom: 60px; }
      .ab-deity-card { text-align: center; padding: 24px 16px; background: linear-gradient(160deg, #1a0606 0%, #0e0101 100%); border: 1px solid rgba(232,196,106,0.1); clip-path: polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%); display: flex; flex-direction: column; align-items: center; gap: 10px; aspect-ratio: 1; justify-content: center; }
      .ab-deity-om { font-family: "Cinzel Decorative", cursive; font-size: 26px; background: linear-gradient(180deg, #fff8e7, var(--gold-bright), var(--gold-mid)); -webkit-background-clip: text; -webkit-text-fill-color: transparent; }
      .ab-deity-name { font-family: "Cinzel", serif; font-size: 9px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; color: rgba(232,196,106,0.6); }

      /* Quote banner */
      .ab-quote { background: linear-gradient(135deg, #2e0c0c 0%, #1c0505 100%); border-top: 1px solid rgba(201,151,58,0.18); border-bottom: 1px solid rgba(201,151,58,0.18); padding: 48px 28px; text-align: center; position: relative; overflow: hidden; margin-bottom: 0; }
      .ab-quote::before { content: 'ॐ'; position: absolute; font-family: "Cinzel Decorative", cursive; font-size: 180px; color: rgba(232,196,106,0.03); left: 50%; top: 50%; transform: translate(-50%, -50%); pointer-events: none; }
      .ab-quote-text { font-family: "EB Garamond", serif; font-size: 22px; font-style: italic; color: rgba(245,228,176,0.7); max-width: 680px; margin: 0 auto 14px; line-height: 1.7; position: relative; }
      .ab-quote-attr { font-family: "Cinzel", serif; font-size: 9px; letter-spacing: 2.5px; text-transform: uppercase; color: rgba(232,196,106,0.35); position: relative; }

      /* ══ TOAST ══ */
      .ab-toast-wrap { position: fixed; top: 28px; right: 28px; z-index: 4000; animation: abToastIn 0.38s cubic-bezier(0.23,1,0.32,1) both; }
      @keyframes abToastIn { from { opacity: 0; transform: translateX(60px); } to { opacity: 1; transform: none; } }
      .ab-toast { width: 340px; background: linear-gradient(145deg, #FDF3E3, #faf0dc, #f5e8cc); border: 1px solid var(--border); border-radius: 2px; box-shadow: 0 12px 32px rgba(26,4,4,0.14); padding: 0 0 14px; overflow: hidden; position: relative; }
      .ab-toast-bar { height: 3px; background: linear-gradient(90deg,transparent,var(--accent) 20%,var(--accent) 80%,transparent); margin-bottom: 16px; }
      .ab-tc { position: absolute; width: 9px; height: 9px; border-color: rgba(201,151,58,0.4); border-style: solid; }
      .ab-tc.tl { top: 11px; left: 11px; border-width: 1px 0 0 1px; }
      .ab-tc.tr { top: 11px; right: 11px; border-width: 1px 1px 0 0; }
      .ab-tc.bl { bottom: 7px; left: 11px; border-width: 0 0 1px 1px; }
      .ab-tc.br { bottom: 7px; right: 11px; border-width: 0 1px 1px 0; }
      .ab-toast-medal { position: absolute; left: 18px; top: 26px; width: 34px; height: 34px; border-radius: 50%; border: 1px solid; display: flex; align-items: center; justify-content: center; }
      .ab-toast-spin { position: absolute; inset: -3px; border-radius: 50%; border: 1px dashed rgba(201,151,58,0.28); animation: abSpin 8s linear infinite; }
      @keyframes abSpin { to { transform: rotate(360deg); } }
      .ab-toast-body { padding: 0 40px 0 64px; }
      .ab-toast-row { display: flex; align-items: center; gap: 6px; margin-bottom: 5px; }
      .ab-toast-title { font-family: "Cinzel", serif; font-size: 9.5px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; flex: 1; }
      .ab-toast-rule { height: 1px; margin-bottom: 6px; }
      .ab-toast-msg { font-family: "EB Garamond", serif; font-style: italic; font-size: 14px; color: rgba(74,26,8,0.72); line-height: 1.5; }
      .ab-toast-foot { height: 1px; margin: 12px 18px 0; background: linear-gradient(90deg,transparent,rgba(201,151,58,0.22),transparent); }
      .ab-toast-x { position: absolute; top: 20px; right: 13px; background: none; border: none; font-size: 12px; cursor: pointer; opacity: 0.4; transition: opacity 0.2s; }
      .ab-toast-x:hover { opacity: 0.85; }

      /* ══ RESPONSIVE ══ */
      @media (max-width: 900px) {
        .ab-features { grid-template-columns: repeat(2, 1fr); }
        .ab-deities { grid-template-columns: repeat(2, 1fr); }
      }
      @media (max-width: 640px) {
        .ab-hero { grid-template-columns: 32px 1fr 32px; }
        .ab-hlc-om { font-size: 14px; }
        .ab-hero-title { font-size: 20px; }
        .ab-features { grid-template-columns: 1fr; }
        .ab-info-strip { grid-template-columns: 1fr; }
        .ab-deities { grid-template-columns: repeat(2, 1fr); }
        .ab-feature-card { clip-path: none; border-radius: 14px; }
        .ab-info-card { clip-path: none; border-radius: 14px; }
        .ab-body { padding: 36px 16px 60px; }
        .ab-toast-wrap { top: auto; bottom: 14px; right: 14px; left: 14px; }
        .ab-toast { width: 100%; border-radius: 12px; }
      }
    `}</style>
  );
}

/* ─────────────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────────────── */
export default function About() {
  const { t } = useTranslation();
  const [logoFace, setLogoFace] = useState("om");
  const [toast, setToast] = useState(null);

  const showToast = (message, type = "info") => setToast({ message, type });

  useEffect(() => {
    const timer = setInterval(() => setLogoFace(f => f === "om" ? "logo" : "om"), 3000);
    return () => clearInterval(timer);
  }, []);

  const features = [
    { icon: <FaLeaf />,  title: t("abFeature1Title") || "Ancient Heritage"},
    { icon: <FaFire />,  title: t("abFeature2Title") || "Daily Rituals" },
    { icon: <FaStar />,  title: t("abFeature3Title") || "Sacred Festivals"},
    { icon: <FaHeart />, title: t("abFeature4Title") || "Community Service"},
    { icon: <FaPray />,  title: t("abFeature5Title") || "Vazhipadu"},
    { icon: <FaStar />,  title: t("abFeature6Title") || "Open to All"},
  ];

  const deities = [
    t("abDeity1")  || "Bhadrakali",
    t("abDeity2")  || "Ganapathi",
    t("abDeity3")  || "Nagarajavu",
    t("abDeity4")  || "Nagayakshi",
    t("abDeity5")  || "Maninagam",
    t("abDeity6")  || "Karinagam",
    t("abDeity7")  || "Akhila Sarpam",
    t("abDeity8")  || "Rakshassu",
    t("abDeity9")  || "Yakshi",
    t("abDeity10") || "Yogeeswaran",
  ];

  return (
    <>
      <AbStyles />
      <Navbar />

      {/* ══ HERO ══ */}
      <div className="ab-hero">
        <div className="ab-hero-bg" />
        <div className="ab-hero-rings">
          {[220, 170, 125, 85].map((s, i) => (
            <div key={i} className="ab-hero-ring" style={{ width: s, height: s, animationDelay: `${i * 0.5}s` }} />
          ))}
        </div>

        {/* Left side column */}
        <div className="ab-hero-side-col ab-side-left">
          <div className="ab-hlc-top-ornament">
            {[...Array(7)].map((_, i) => (
              <div key={i} className="ab-hlc-tick" style={{ opacity: 0.15 + i * 0.1, height: 3 + i * 2 }} />
            ))}
          </div>
          <div className="ab-hlc-om">ॐ</div>
          <div className="ab-hlc-line" />
          <span className="ab-hlc-symbol">◈</span>
          <div className="ab-hlc-line" />
          <div className="ab-hlc-bot-ornament">
            {[...Array(7)].map((_, i) => (
              <div key={i} className="ab-hlc-tick" style={{ opacity: 0.85 - i * 0.1, height: 3 + (6 - i) * 2 }} />
            ))}
          </div>
        </div>

        {/* Center */}
        <div className="ab-hero-center">
          <div className="ab-hero-medallion">
            <div className="ab-hero-med-ring" />
            <div className="ab-hero-med-ring ab-hero-med-ring-2" />
            <div className={`ab-hero-face ${logoFace === "om" ? "visible" : ""}`}>
              <span className="ab-hero-om">ॐ</span>
            </div>
            <div className={`ab-hero-face ${logoFace === "logo" ? "visible" : ""}`}>
              <img src="./logo.png" alt="Temple" className="ab-hero-logo-img" />
            </div>
            <div className="ab-hero-med-halo" />
          </div>

          <div className="ab-hero-eyebrow">✦ {t("templeName") || "Kodungallur Kavil Temple"} ✦</div>
          <h1 className="ab-hero-title">{t("aboutTitle") || "About the Temple"}</h1>
          <p className="ab-hero-sub">{t("aboutSub") || "History, traditions & the divine spirit of Bhadrakali"}</p>

          <div className="ab-hero-rule" />
        </div>

        {/* Right side column */}
        <div className="ab-hero-side-col ab-side-right">
          <div className="ab-hlc-top-ornament">
            {[...Array(7)].map((_, i) => (
              <div key={i} className="ab-hlc-tick" style={{ opacity: 0.15 + i * 0.1, height: 3 + i * 2 }} />
            ))}
          </div>
          <div className="ab-hlc-om">ॐ</div>
          <div className="ab-hlc-line" />
          <span className="ab-hlc-symbol">◈</span>
          <div className="ab-hlc-line" />
          <div className="ab-hlc-bot-ornament">
            {[...Array(7)].map((_, i) => (
              <div key={i} className="ab-hlc-tick" style={{ opacity: 0.85 - i * 0.1, height: 3 + (6 - i) * 2 }} />
            ))}
          </div>
        </div>
      </div>

      {/* ══ BODY ══ */}
      <div className="ab-body">
        <div className="ab-inner">

          {/* History */}
          <div className="ab-sec-head">
            <div className="ab-sec-eyebrow">✦ {t("abStoryEyebrow") || "Our Story"} ✦</div>
            <h2 className="ab-sec-title">{t("abStoryTitle") || "Ancient Temple, Eternal Devotion"}</h2>
            <div className="ab-sec-rule" />
          </div>
          <div className="ab-history">
            <p>{t("abHistoryP1") || "Kodungallor Kavil Sree Bhadra Kali Devi Temple, nestled in the heart of Pandanad, Chengannur, is one of the most ancient and spiritually significant Bhadrakali temples in Kerala."}</p>
            <p>{t("abHistoryP2") || "The temple follows strict Tantric traditions rooted in Kerala's classical temple culture, with daily rituals, seasonal festivals, and a legacy of devotion passed down through generations."}</p>
          </div>

          {/* Feature cards */}
          <div className="ab-sec-head">
            <div className="ab-sec-eyebrow">✦ {t("abFeaturesEyebrow") || "What We Offer"} ✦</div>
            <h2 className="ab-sec-title">{t("abFeaturesTitle") || "Temple Traditions & Services"}</h2>
            <div className="ab-sec-rule" />
          </div>
          <div className="ab-features">
            {features.map((f, i) => (
              <div key={i} className="ab-feature-card">
                <div className="ab-feature-icon">{f.icon}</div>
                <div className="ab-feature-title">{f.title}</div>
                <div className="ab-feature-text">{f.text}</div>
              </div>
            ))}
          </div>

          {/* Deities */}
          <div className="ab-sec-head">
            <div className="ab-sec-eyebrow">✦ {t("abDeitiesEyebrow") || "Presiding Deities"} ✦</div>
            <h2 className="ab-sec-title">{t("abDeitiesTitle") || "Divine Presences"}</h2>
            <div className="ab-sec-rule" />
          </div>
          <div className="ab-deities">
            {deities.map((d, i) => (
              <div key={i} className="ab-deity-card">
                <div className="ab-deity-om">ॐ</div>
                <div className="ab-deity-name">{d}</div>
              </div>
            ))}
          </div>

          {/* Info strip */}
          <div className="ab-sec-head">
            <div className="ab-sec-eyebrow">✦ {t("abVisitEyebrow") || "Practical Info"} ✦</div>
            <h2 className="ab-sec-title">{t("abVisitTitle") || "Visit the Temple"}</h2>
            <div className="ab-sec-rule" />
          </div>
          <div className="ab-info-strip">
            <div className="ab-info-card">
              <div className="ab-info-card-title"><FaClock /> {t("abTimingsTitle") || "Pooja Timings"}</div>
              <div className="ab-info-row"><FaStar /> {t("abTimingNadaThurakkalAM")  || "Nada Thurakkal — 06:00 AM"}</div>
              <div className="ab-info-row"><FaStar /> {t("abTimingGanapathiHomam")   || "Ganapathi Homam — 06:30 AM"}</div>
              <div className="ab-info-row"><FaStar /> {t("abTimingUshaPooja")        || "Usha Pooja — 07:00 AM"}</div>
              <div className="ab-info-row"><FaStar /> {t("abTimingThudarnullaPooja") || "Thudarnulla Pooja — 07:30 AM"}</div>
              <div className="ab-info-row"><FaStar /> {t("abTimingBhagavathaParayanam") || "Bhagavatha Parayanam — 08:00 AM"}</div>
              <div className="ab-info-row"><FaStar /> {t("abTimingUchaPooja")        || "Ucha Pooja — 09:00 AM"}</div>
              <div className="ab-info-row"><FaStar /> {t("abTimingNadaAdakkalAM")    || "Nada Adakkal — 10:00 AM"}</div>
              <div className="ab-info-row"><FaStar /> {t("abTimingAnnadhanam")       || "Annadhanam — 12:00 PM"}</div>
              <div className="ab-info-row"><FaStar /> {t("abTimingNadaThurakkalPM")  || "Nada Thurakkal — 06:00 PM"}</div>
              <div className="ab-info-row"><FaStar /> {t("abTimingDeeparadhana")     || "Deeparadhana — 07:00 PM"}</div>
              <div className="ab-info-row"><FaStar /> {t("abTimingBhagavathiSeva")   || "Bhagavathi Seva — 07:30 PM"}</div>
              <div className="ab-info-row"><FaStar /> {t("abTimingNadaAdakkalPM")    || "Nada Adakkal"}</div>
            </div>
            <div className="ab-info-card">
              <div className="ab-info-card-title"><FaMapMarkerAlt /> {t("abLocationTitle") || "Location & Contact"}</div>
              <div className="ab-info-row"><FaMapMarkerAlt /> {t("abAddress") || "Kodungallor Kavil Sree Bhadra Kali Devi Temple, 8H9C+W79, Parambathurpadi - Budhanoor Rd, Pandanad, Kerala 689506"}</div>
              <div className="ab-info-row"><FaPhone /> {t("abPhone") || "+91 94470 14544"}</div>
              <div className="ab-info-row"><FaEnvelope /> {t("abEmail") || "temple@kavil.org"}</div>
              <div className="ab-info-row"><FaClock /> {t("abHours") || "Morning: 06:00 AM – 10:00 AM · Evening: 06:00 PM – Nada Adakkal"}</div>
            </div>
          </div>

        </div>
      </div>

      {/* ══ QUOTE BANNER ══ */}
      <div className="ab-quote">
        <div className="ab-quote-text">"{t("abQuote") || "She who is the source of all power, protect us — as you have protected this land since time immemorial."}"</div>
        <div className="ab-quote-attr">— {t("abQuoteAttr") || "Temple Invocation"}</div>
      </div>

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      <Footer />
    </>
  );
}